import config from "../../config/config.json";
import { useDocumentTitle } from "../../hooks/use-document-title";
import "./PrivacyPolicyPage.scss";

export default function PrivacyPolicyPage() {
  useDocumentTitle("Privacy Policy");

  const contactEmail = config.contactEmail;
  return (
    <div className="privacy-policy-page-container">
      <h1>Privacy Policy</h1>
      <p>Last updated: July 29, 2025</p>

      <h2>1. Introduction</h2>
      <p>
        Calendar Automation ("Service", "we", "our", "us") respects your privacy
        and is committed to protecting the personal information you share with
        us. This Privacy Policy explains what information we collect, how we use
        it, and your rights regarding your information.
      </p>

      <h2>2. Information We Collect</h2>
      <ul>
        <li>
          <strong>Google Account Information:</strong> When you sign in with
          Google, we receive your Google Account ID and an OAuth access token.
        </li>
        <li>
          <strong>Calendar Data:</strong> With your explicit permission, we may
          read your calendar events and create new events on your behalf.
        </li>
      </ul>

      <h2>3. How We Use Your Information</h2>
      <p>We use the information we collect solely to:</p>
      <ul>
        <li>Authenticate you via Firebase and Google OAuth.</li>
        <li>
          Retrieve your upcoming events when you ask the Service a question.
        </li>
        <li>
          Create or update calendar events only when you explicitly request it.
        </li>
      </ul>

      <h2>4. Data Storage &amp; Retention</h2>
      <p>
        Your OAuth access token is stored locally in your browser's local
        storage. We do <em>not</em> store your access token, calendar events, or
        any other personal data on our servers. All data remains on your device
        and within Google's systems. If you clear your browser data or revoke
        our access in your Google Account settings, the token will be removed
        and our access will end.
      </p>

      <h2>5. Data Sharing</h2>
      <p>
        We do <strong>not</strong> share, sell, rent, or trade your personal
        information or calendar data with any third parties. The only external
        parties involved are the trusted services required to operate the
        application, namely Google (for authentication and Calendar API access)
        and Firebase (for authentication infrastructure).
      </p>

      <h2>6. Third-Party Services</h2>
      <p>
        The Service relies on the following third-party services, each of which
        has its own privacy practices:
      </p>
      <ul>
        <li>
          <strong>Google Calendar API:</strong> Used to read and manage your
          calendar events. Usage is subject to the Google API Services User Data
          Policy.
        </li>
        <li>
          <strong>Firebase Authentication:</strong> Provides secure user
          authentication. Usage is subject to Google's Firebase Terms.
        </li>
      </ul>

      <h2>7. Security</h2>
      <p>
        We use industry-standard security measures such as HTTPS to protect the
        transmission of information. Because we do not store your personal data
        on our servers, exposure is minimized. Nevertheless, no internet
        transmission is completely secure, so we cannot guarantee absolute
        security.
      </p>

      <h2>8. Your Choices &amp; Controls</h2>
      <ul>
        <li>
          You may revoke Calendar Automation's access to your Google Account at
          any time via your Google Account security settings.
        </li>
        <li>
          You can delete the local storage entry named
          <code>"calauto-google-access-token"</code> in your browser to remove
          the stored token.
        </li>
      </ul>

      <h2>9. Children's Privacy</h2>
      <p>
        The Service is not directed to children under 13. We do not knowingly
        collect personal information from children. If you believe we have done
        so inadvertently, please contact us and we will delete such information
        promptly.
      </p>

      <h2>10. Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. We will notify you
        of any material changes by posting the new policy on this page. The
        "Last updated" date at the top indicates when the policy was last
        revised.
      </p>

      <h2>11. Contact Us</h2>
      <p>
        If you have any questions about this Privacy Policy or the Service,
        please contact us at{" "}
        <a href={`mailto:${contactEmail}`}>{contactEmail}</a>.
      </p>
    </div>
  );
}
